import type { CSSProperties, ReactNode } from 'react';
import { COLORS, RADIUS } from '../theme';

export function Card({ children, style, title }: { children: ReactNode; style?: CSSProperties; title?: string }) {
  return (
    <div style={{ ...cardStyle, ...style }}>
      {title ? <div style={cardTitle}>{title}</div> : null}
      {children}
    </div>
  );
}

export function SectionHeader({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div style={sectionHeaderStyle}>
      <h2 style={sectionTitleStyle}>{children}</h2>
      {right ? <div>{right}</div> : null}
    </div>
  );
}

export function Metric({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div style={{ padding: '4px 0' }}>
      <div style={metricLabel}>{label}</div>
      <div style={{ ...metricValue, color: color ?? COLORS.text }}>{value}</div>
      {sub ? <div style={metricSub}>{sub}</div> : null}
    </div>
  );
}

export function KV({ k, v, color }: { k: string; v: ReactNode; color?: string }) {
  return (
    <div style={kvRow}>
      <span style={kvKey}>{k}</span>
      <span style={{ ...kvVal, color: color ?? COLORS.text }}>{v}</span>
    </div>
  );
}

export function Button({
  children, onClick, disabled, variant = 'primary', type = 'button',
}: {
  children: ReactNode; onClick?: () => void; disabled?: boolean; variant?: 'primary' | 'ghost'; type?: 'button' | 'submit';
}) {
  const bg = variant === 'primary' ? COLORS.accent : COLORS.surfaceAlt;
  const fg = variant === 'primary' ? '#0A0B0D' : COLORS.text;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        background: bg, color: fg, border: variant === 'ghost' ? `1px solid ${COLORS.border}` : 'none',
        borderRadius: RADIUS.input, padding: '8px 14px', fontSize: 13, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

export function Pill({ children, color = COLORS.textSecondary }: { children: ReactNode; color?: string }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: RADIUS.chip,
      background: 'rgba(255,255,255,0.04)', color, fontSize: 11, fontWeight: 500,
    }}>{children}</span>
  );
}

export function Grid({ children, cols = 4, gap = 20 }: { children: ReactNode; cols?: number; gap?: number }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap }}>
      {children}
    </div>
  );
}

const cardStyle: CSSProperties = {
  background: COLORS.surface, borderRadius: RADIUS.card,
  border: `1px solid ${COLORS.border}`, padding: 20,
};
const cardTitle: CSSProperties = {
  color: COLORS.textMuted, fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase',
  fontWeight: 600, marginBottom: 12,
};
const sectionHeaderStyle: CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginBottom: 12, marginTop: 24,
};
const sectionTitleStyle: CSSProperties = {
  color: COLORS.text, fontSize: 18, fontWeight: 600, margin: 0,
};
const metricLabel: CSSProperties = {
  color: COLORS.textMuted, fontSize: 11, letterSpacing: 0.4, textTransform: 'uppercase',
  fontWeight: 600, marginBottom: 4,
};
const metricValue: CSSProperties = { fontSize: 24, fontWeight: 600, letterSpacing: -0.4 };
const metricSub: CSSProperties = { color: COLORS.textMuted, fontSize: 11, marginTop: 4 };
const kvRow: CSSProperties = {
  display: 'flex', justifyContent: 'space-between',
  padding: '6px 0', borderBottom: `1px dashed ${COLORS.border}`,
};
const kvKey: CSSProperties = { color: COLORS.textSecondary, fontSize: 13 };
const kvVal: CSSProperties = { fontSize: 13, fontWeight: 500 };
